import React, {
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';
import { routes, URL } from 'appConstants';
import {
  ButtonV4,
  Link,
  NoData,
  Preloader,
} from 'components';
import { useRequest } from 'hooks';
import {
  GetResponse,
  RequestStatus,
  Resolution,
} from 'types';
import {
  _t,
  _t_back,
  openApplicationForm,
  openDocumentsModal,
} from 'utils';

import banner from 'assets/img/main-v4/hero-curve.png';
import bg1 from 'assets/img/resolutions-v4/bg-1-art.png';
import bg2 from 'assets/img/resolutions-v4/bg-2-art.png';
import bg3 from 'assets/img/resolutions-v4/bg-3-art.png';
import bg4 from 'assets/img/resolutions-v4/bg-4-art.png';
import libraryPromo from 'assets/img/resolutions-v4/library-promo.png';
import iconSend from 'assets/img/resolutions-v4/icon-send.svg';
import iconArrow from 'assets/img/resolutions-v4/icon-arrow.svg';
import iconSearch from 'assets/img/header-v4/icon-search.svg';
import iconDoc from 'assets/img/resolutions-v4/icon-docs.svg';
import iconPlus from 'assets/img/resolutions-v4/icon-plus.svg';

import {
  MOSPROM_ORG,
  SEE_ALSO,
  emptyLibraryFilters,
  getResolutionOrg,
  getResolutionRegion,
  hasActiveLibraryFilters,
  matchLibraryFilters,
  matchResolutionSearch,
} from '../shared';
import { LIBRARY_MATERIALS } from '../libraryMaterials';
import LibraryFilters from '../LibraryFilters';
import ServiceTextPopup from '../ServiceTextPopup';
import {
  ANALYTICS_DIRECTIONS,
  AnalyticsPopup,
  ServiceDirection,
} from '../analyticsSupport';
import { BUYER_EXTRA_DIRECTIONS } from '../buyerProgram';
import {
  SUPPORT_COUNTERPARTY,
  SUPPORT_COUNTERPARTY_LEAD,
  SUPPORT_INTRO,
  SUPPORT_LEAD,
} from '../supportInfo';
import {
  EDUCATION_FORMATS,
  EDUCATION_FORMATS_TITLE,
  EDUCATION_LEAD,
  EDUCATION_SCOPE,
} from '../educationInfo';
import styles from './styles.module.scss';

type DisplayCard = {
  id: number;
  title: string;
  region: string;
  org: string;
  href?: string;
  downloadLabel?: string;
  slug?: string;
};

const LIBRARY_PROMO_TEXT = 'Полный перечень существующих мер поддержки для экспортеров с возможностью быстрого доступа';

const PAGE_SIZE = 7;
const FETCH_LIMIT = 100;
const SERVICES = [
  {
    id: 'analytics',
    media: bg1,
    title: 'Аналитическая\nподдержка',
    body: 'Аналитическая поддержка столичных экспортеров: аналитика, исследования и экспортные стратегии по перспективным направлениям экспорта в приоритетных высокотехнологичных отраслях промышленности',
    directions: ANALYTICS_DIRECTIONS,
  },
  {
    id: 'buyer',
    media: bg2,
    title: 'Байерская\nпрограмма',
    body: 'программа центра направлена на подбор контрагентов и...',
    directionsTitle: 'Центр организует для московских производителей программу по направлениям:',
    directions: [
      { title: 'Выездные и реверсные бизнес-миссии (online и offline)' },
      { title: 'Технологические туры' },
      { title: 'Предпродажные мероприятия (online и offline), включая MatchMaking' },
      { title: 'Организация участия в Международных отраслевых зарубежных выставка на объединенном стенде под брендом MADE IN MOSCOW' },
      ...BUYER_EXTRA_DIRECTIONS,
    ] as ServiceDirection[],
    funding: 'Участие столичных компаний в программе на 100 % финансируется за счет средств Правительства Москвы (за исключением проезда и проживания)',
  },
  {
    id: 'support',
    media: bg3,
    title: 'Информационно-консультационная\nподдержка',
    titleLong: true,
    body: SUPPORT_INTRO,
    directions: [
      { title: SUPPORT_COUNTERPARTY },
    ] as ServiceDirection[],
  },
  {
    id: 'education',
    media: bg4,
    title: 'Обучение',
    body: EDUCATION_LEAD,
    directionsTitle: EDUCATION_FORMATS_TITLE,
    directions: EDUCATION_FORMATS.map((title) => ({ title })) as ServiceDirection[],
  },
];

const ResolutionsHome = () => {
  const { data, request, status } = useRequest<GetResponse<Resolution[]>>({
    errback: () => undefined,
  });
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filters, setFilters] = useState(emptyLibraryFilters());
  const [page, setPage] = useState(0);
  const [directionPopup, setDirectionPopup] = useState<AnalyticsPopup | null>(null);
  const libraryRef = useRef<HTMLElement>(null);

  useEffect(() => {
    request({
      method: 'get',
      url: URL.RESOLUTIONS.GET,
      params: {
        limit: FETCH_LIMIT,
        offset: 0,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (event: FormEvent) => {
    event.preventDefault();
    setAppliedSearch(search.trim());
    setPage(0);
  };

  const onFiltersChange = (next: ReturnType<typeof emptyLibraryFilters>) => {
    setFilters(next);
    setPage(0);
  };

  const onPageChange = (nextPage: number) => {
    setPage(nextPage);
    libraryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const isQueryMode = Boolean(appliedSearch) || hasActiveLibraryFilters(filters);
  const allResolutions = data?.results || [];

  const filteredItems = useMemo(() => {
    if (!isQueryMode) {
      return null;
    }
    return allResolutions
      .filter((item) => matchResolutionSearch(item, appliedSearch))
      .filter((item) => matchLibraryFilters(item, filters));
  }, [allResolutions, appliedSearch, filters, isQueryMode]);

  const defaultCards: DisplayCard[] = useMemo(() => (
    LIBRARY_MATERIALS.map((item) => ({
      id: item.id,
      title: _t(item.title),
      region: _t('МОСКВА'),
      org: _t(MOSPROM_ORG),
      href: item.href,
      downloadLabel: item.downloadLabel,
    }))
  ), []);

  const queryCards: DisplayCard[] = useMemo(() => (
    (filteredItems || []).map((item) => ({
      id: item.id,
      title: _t_back(item, 'res_title'),
      region: getResolutionRegion(item),
      org: getResolutionOrg(item),
      slug: item.slug,
    }))
  ), [filteredItems]);

  const sourceCards = isQueryMode ? queryCards : defaultCards;
  const total = sourceCards.length;
  const pageItemsRaw = sourceCards.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const cardsTop = pageItemsRaw.slice(0, 3);
  const cardsBottom = pageItemsRaw.slice(3, 7);
  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;
  const isLoadingQuery = isQueryMode && status === RequestStatus.REQUEST;

  const pageItems = useMemo(() => {
    if (!pageCount || pageCount <= 1) {
      return [] as Array<number | 'ellipsis'>;
    }
    if (pageCount <= 5) {
      return Array.from({ length: pageCount }, (_, index) => index);
    }
    return [0, 1, 2, 3, 'ellipsis' as const, pageCount - 1];
  }, [pageCount]);

  const openMaterial = (item: DisplayCard) => {
    if (!item.href) {
      return;
    }
    openDocumentsModal([{
      id: item.id,
      title: item.title,
      href: item.href,
      downloadLabel: item.downloadLabel,
    }]);
  };

  const renderLibraryCard = (item: DisplayCard) => {
    const detail = (
      <>
        <span className={styles.libraryDetailLabel}>{_t('Подробнее')}</span>
        <span className={styles.libraryDetailIcon} aria-hidden>
          <img src={iconArrow} alt="" width={12} height={12} />
        </span>
      </>
    );

    return (
      <li key={item.id} className={styles.libraryCard}>
        <div className={styles.libraryMeta}>
          <span className={styles.libraryTag}>{item.region}</span>
          <h3 className={styles.libraryCardTitle}>{item.title}</h3>
        </div>
        <div className={styles.libraryFooter}>
          <p className={styles.libraryCardText}>{item.org}</p>
          {item.slug ? (
            <Link
              to={`${routes.resolutions.root}/${item.slug}`}
              className={styles.libraryDetailBtn}
            >
              {detail}
            </Link>
          ) : (
            <button
              type="button"
              className={styles.libraryDetailBtn}
              onClick={() => openMaterial(item)}
            >
              {detail}
            </button>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.banner} aria-hidden>
        <img src={banner} alt="" />
      </div>

      <section className={styles.head}>
        <h1 className={styles.title}>{_t('Услуги')}</h1>
        <nav className={styles.crumbs} aria-label={_t('Навигация')}>
          <Link to={routes.main.root} className={styles.crumbLink}>{_t('Главная')}</Link>
          <span className={styles.crumbLine} aria-hidden />
          <span className={styles.crumbCurrent}>{_t('Экспортерам')}</span>
        </nav>
      </section>

      <section className={styles.services}>
        {SERVICES.map((item) => (
          <article
            key={item.id}
            className={styles.serviceRow}
          >
            <div className={styles.serviceMedia}>
              <div className={styles.serviceArt} aria-hidden>
                <img src={item.media} alt="" className={styles.serviceArtImg} />
              </div>
              <h2
                className={cx(
                  styles.serviceTitle,
                  item.titleLong && styles.serviceTitleLong,
                )}
              >
                {_t(item.title)}
              </h2>
              <button
                type="button"
                className={styles.applyBtn}
                onClick={() => openApplicationForm({
                  type: 'service',
                  id: item.id,
                  title: item.title,
                  path: `${routes.resolutions.service.root}/${item.id}`,
                })}
              >
                <span className={styles.applyLabel}>{_t('Оформить заявку')}</span>
                <span className={styles.applyIcon} aria-hidden>
                  <img src={iconPlus} alt="" width={16} height={16} />
                </span>
              </button>
            </div>
            <div className={styles.serviceInfo}>
              <div className={styles.serviceBody}>
                <p className={styles.serviceText}>{_t(item.body)}</p>
                {item.id === 'education' ? (
                  <p className={styles.serviceText}>{_t(EDUCATION_SCOPE)}</p>
                ) : null}
                {item.directionsTitle ? (
                  <p className={styles.serviceDirectionsTitle}>{_t(item.directionsTitle)}</p>
                ) : null}
                {item.directions?.length ? (
                  <ul className={styles.serviceDirections}>
                    {item.directions.map((line) => (
                      <li key={line.title}>
                        {line.popup ? (
                          <button
                            type="button"
                            className={styles.serviceDirectionBtn}
                            onClick={() => setDirectionPopup(line.popup || null)}
                          >
                            {_t(line.title)}
                          </button>
                        ) : (
                          _t(line.title)
                        )}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {item.id === 'support' ? (
                  <>
                    <p className={styles.serviceText}>{_t(SUPPORT_COUNTERPARTY_LEAD)}</p>
                    <p className={styles.serviceText}>
                      {_t(SUPPORT_LEAD).replace(/:\s*$/, '')}
                    </p>
                  </>
                ) : null}
                {item.funding ? (
                  <p className={styles.serviceText}>{_t(item.funding)}</p>
                ) : null}
              </div>
              <div className={styles.serviceActions}>
                <Link
                  to={`${routes.resolutions.service.root}/${item.id}`}
                  className={styles.detailBtn}
                >
                  <span className={styles.detailLabel}>{_t('Подробнее')}</span>
                  <span className={styles.detailIcon} aria-hidden>
                    <img src={iconArrow} alt="" width={12} height={12} />
                  </span>
                </Link>
                <button
                  type="button"
                  className={styles.docsBtn}
                  onClick={() => openDocumentsModal()}
                >
                  <span className={styles.docsIcon} aria-hidden>
                    <img src={iconDoc} alt="" width={8} height={14} />
                  </span>
                  <span className={styles.docsLabel}>{_t('Смотреть документы')}</span>
                </button>
              </div>
            </div>
          </article>
        ))}
      </section>

      <section className={styles.testBand}>
        <h2 className={styles.testTitle}>{_t('Оценка экспортного потенциала ТЕСТ')}</h2>
        <ButtonV4
          to={routes.auth.exportTest.root}
          label="Оставить заявку"
          variant="outline"
          tone="onDark"
          className={styles.testCta}
          iconSrc={iconSend}
          iconSize={20}
        />
      </section>

      <section className={styles.library} ref={libraryRef}>
        <div className={styles.libraryHead}>
          <h2 className={styles.libraryTitle}>{_t('Библиотека экспортера')}</h2>
          <div className={styles.libraryToolbar}>
            <form className={styles.search} onSubmit={onSearch}>
              <input
                className={styles.searchInput}
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={_t('Введите наименование меры поддержки')}
                aria-label={_t('Поиск мер поддержки')}
              />
              <button type="submit" className={styles.searchBtn} aria-label={_t('Найти')}>
                <img src={iconSearch} alt="" width={14} height={14} />
              </button>
            </form>
            <LibraryFilters value={filters} onChange={onFiltersChange} />
          </div>
        </div>

        <Preloader isLoading={isLoadingQuery}>
          <div className={styles.libraryBody}>
            {isQueryMode && pageItemsRaw.length ? (
              <ul className={styles.libraryGrid}>
                {pageItemsRaw.map(renderLibraryCard)}
              </ul>
            ) : null}
            {!isQueryMode ? (
              <>
                <ul className={styles.libraryRowTop}>
                  <li className={styles.libraryPromo}>
                    <img src={libraryPromo} alt="" className={styles.libraryPromoImg} />
                    <p className={styles.libraryPromoText}>{_t(LIBRARY_PROMO_TEXT)}</p>
                  </li>
                  {cardsTop.map(renderLibraryCard)}
                </ul>
                {cardsBottom.length ? (
                  <ul className={styles.libraryRowBottom}>
                    {cardsBottom.map(renderLibraryCard)}
                  </ul>
                ) : null}
              </>
            ) : null}
          </div>
          {isQueryMode && !isLoadingQuery && !pageItemsRaw.length ? (
            <NoData count={0} className={styles.libraryEmpty} />
          ) : null}
          {!isQueryMode && !pageItemsRaw.length ? (
            <NoData count={0} className={styles.libraryEmpty} />
          ) : null}
        </Preloader>

        {pageCount > 1 && !isLoadingQuery ? (
          <nav className={styles.pagination} aria-label={_t('Пагинация')}>
            {pageItems.map((item, index) => {
              if (item === 'ellipsis') {
                const key = `ellipsis-${pageItems[index - 1]}-${pageItems[index + 1]}`;
                return <span key={key} className={styles.pageEllipsis}>...</span>;
              }
              return (
                <button
                  key={item}
                  type="button"
                  className={item === page ? styles.pageBtnActive : styles.pageBtn}
                  onClick={() => onPageChange(item)}
                  aria-current={item === page ? 'page' : undefined}
                >
                  {item + 1}
                </button>
              );
            })}
            {page < pageCount - 1 ? (
              <button
                type="button"
                className={styles.pageNext}
                onClick={() => onPageChange(page + 1)}
                aria-label={_t('Вперёд')}
              >
                <span aria-hidden />
              </button>
            ) : null}
          </nav>
        ) : null}
      </section>

      <section className={styles.seeAlso}>
        <h2 className={styles.seeTitle}>{_t('Смотрите также')}</h2>
        <div className={styles.seeGrid}>
          {SEE_ALSO.map((item) => (
            <div
              key={item.id}
              className={styles.seeCard}
              style={{ backgroundImage: `url(${item.image})` }}
            >
              <div className={styles.seeCardTitle}>{_t(item.title)}</div>
              <ButtonV4
                to={item.to}
                label="Перейти в раздел"
                variant="solid"
                tone="onLight"
                iconSrc={iconArrow}
              />
            </div>
          ))}
        </div>
      </section>
      <ServiceTextPopup
        item={directionPopup}
        onClose={() => setDirectionPopup(null)}
      />
    </div>
  );
};

export default ResolutionsHome;
